<script lang="ts" setup>
import type { FilterEvent } from '@sancare/typescript-commons/src/types/filters'
import type { Division, Facility, MedicalUnit } from '@sancare-commons/store/modules/remote-lists/types'
import _ from 'lodash'
import { computed, onMounted, type Ref, ref, watch } from 'vue'
import { useStore } from 'vuex'

import { capitalize } from '../../misc/string-utils'

const store = useStore()

const model = defineModel<string | null>({ default: null })
const props = withDefaults(defineProps<{
  enableOkBtn?: boolean
  filterDataName: string
  hasPresenceSubFilter?: boolean
  isLegacy?: boolean
  medicalUnitOnly?: boolean
}>(), {
  enableOkBtn: true,
  hasPresenceSubFilter: true,
  isLegacy: false,
  medicalUnitOnly: false,
})

const emit = defineEmits<{
  (e: 'add-filter', filter: FilterEvent): void
}>()

const presence = ref('present')
const facility = ref('')
const division = ref('')
const medicalUnit = ref('')

const facilities: Ref<Facility[]> = computed(() => store.state.remoteLists.facilities.filter((facility: Facility) => facility))
const divisions: Ref<Division[]> = computed(() => store.state.remoteLists.divisions.filter((division: Division) =>
  facility.value === '' || division?.facility?.reference === facility.value
))
const medicalUnits: Ref<MedicalUnit[]> = computed(() => _.uniqBy(store.state.remoteLists.medicalUnits.filter((unit: MedicalUnit) =>
  (division.value === '' || unit.facilityDivision?.reference === division.value)
    && (facility.value === '' || unit.facilityDivision?.facility?.reference === facility.value)
), 'reference'))

function initializeFromInitialValue() {
  if (!model.value) return

  if (props.hasPresenceSubFilter) {
    if (props.filterDataName.startsWith('absent')) {
      presence.value = 'absent'
    } else {
      presence.value = 'present'
    }
  }

  const cleanType = props.filterDataName.replace(/^(present|absent)/, '').toLowerCase()

  // explicit allowlist rather than substring matching: `previousStayMedicalUnit` resolves to the UM
  // field, but `medicalUnitDuration` (exactli) must stay unhandled as it was before
  if (['medicalunit', 'previousstaymedicalunit'].includes(cleanType)) {
    medicalUnit.value = model.value
  } else if (cleanType === 'division') {
    division.value = model.value
  } else if (cleanType === 'facility') {
    facility.value = model.value
  }
}

watch(facility, () => {
  division.value = ''
  medicalUnit.value = ''
})

watch(division, () => {
  medicalUnit.value = ''
})

watch(medicalUnit, () => {
  if (props.enableOkBtn) {
    return
  }
  addFilter()
})

const addFilter = () => {
  let type: string, value: string
  if (medicalUnit.value !== '') {
    type = props.filterDataName
    value = medicalUnit.value
  } else if (division.value !== '' && !props.medicalUnitOnly) {
    type = 'division'
    value = division.value
  } else if (facility.value !== '' && !props.medicalUnitOnly) {
    type = 'facility'
    value = facility.value
  } else {
    return
  }
  if (props.hasPresenceSubFilter) {
    type = presence.value + capitalize(type)
  }

  emit('add-filter', {
    name: props.filterDataName,
    type,
    value,
  })
}

onMounted(()=> {
  initializeFromInitialValue()
})
</script>

<template>
  <div :class="isLegacy ? 'row' : 'mt-1'">
    <select
      v-if="hasPresenceSubFilter"
      id="medical-unit-filter-presence"
      v-model="presence"
      name="presence-select"
      class="form-select"
      :class="isLegacy ? 'col' : 'col-12 mb-1'"
    >
      <option value="present">
        Présent
      </option>
      <option value="absent">
        Absent
      </option>
    </select>
    <select
      v-if="facilities && facilities.length > 1"
      id="medical-unit-filter-facility"
      v-model="facility"
      name="facility-select"
      class="form-select"
      :class="isLegacy ? 'col' : 'col-12 mb-1'"
    >
      <option value="">
        Tous établissements
      </option>
      <option
        v-for="etab in facilities"
        :key="'etab_'+etab.id"
        :value="etab.reference"
      >
        {{ etab.name === '' ? (`Etablissement inconnu${etab.geographicFiness ? (` (${etab.geographicFiness})`) : ''}`) : etab.name }}
      </option>
    </select>
    <select
      v-if="divisions && divisions.length > 1"
      id="medical-unit-filter-division"
      v-model="division"
      name="division-select"
      class="form-select"
      :class="isLegacy ? 'col' : 'col-12 mb-1'"
    >
      <option value="">
        Tous pôles
      </option>
      <option
        v-for="div in divisions"
        :key="'div_'+div.id"
        :value="div.reference"
      >
        {{ div.title === '' ? 'Pôle inconnu' : div.title }}
      </option>
    </select>
    <div
      class="px-0"
      :class="{
        col: isLegacy,
        'col-12': !isLegacy,
        'input-group': enableOkBtn,
      }"
    >
      <select
        id="medical-unit-filter-unit"
        v-model="medicalUnit"
        class="form-select"
        name="medical-unit-select"
      >
        <option value="">
          Toutes unités médicales
        </option>
        <option
          v-for="um in medicalUnits"
          :key="'um_'+ um.id"
          :value="um.reference"
        >
          {{ um.reference }}{{ um.title ? ` - ${um.title}` : '' }}
        </option>
      </select>
      <div
        v-if="enableOkBtn"
        class="input-group-append"
      >
        <button
          id="medical-unit-filter-add"
          class="btn btn-primary"
          type="button"
          @click="addFilter"
        >
          Ok
        </button>
      </div>
    </div>
  </div>
</template>
